/**
 * Triggers the browser's password manager / iOS Keychain save prompt
 * with the correct email + new password pair.
 *
 * Rationale:
 *  - On password-change, the confirmation alert's `current-password` field is
 *    what iOS would otherwise capture, leading Keychain to save the OLD password
 *    with no email.
 *  - On forgot-password and account-creation, the success path navigates via
 *    React Router (no native form submit, no page reload), so Safari/WKWebView
 *    never triggers its save-password heuristic at all.
 *
 * Solution: programmatically build an off-screen <form> populated with the
 * correct `autocomplete="username"` + `autocomplete="new-password"` fields,
 * dispatch a submit event on it so the password manager detects the credential
 * submission, and cancel the default action to avoid navigation.
 *
 * No UX impact: the form is positioned off-screen and removed shortly after.
 */
export function triggerKeychainSave(email: string, password: string): void {
  if (!email || !password) return;
  if (typeof document === 'undefined') return;

  const form = document.createElement('form');
  form.style.cssText =
    'position:fixed;left:-9999px;top:0;width:1px;height:1px;opacity:0;pointer-events:none;';
  form.method = 'post';
  form.action = window.location.href;
  form.setAttribute('aria-hidden', 'true');
  form.addEventListener('submit', (event) => {
    event.preventDefault();
  });

  const usernameInput = document.createElement('input');
  usernameInput.type = 'email';
  usernameInput.name = 'username';
  usernameInput.autocomplete = 'username';
  usernameInput.value = email;
  form.appendChild(usernameInput);

  const passwordInput = document.createElement('input');
  passwordInput.type = 'password';
  passwordInput.name = 'password';
  passwordInput.autocomplete = 'new-password';
  passwordInput.value = password;
  form.appendChild(passwordInput);

  document.body.appendChild(form);

  try {
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
    } else {
      form.dispatchEvent(
        new Event('submit', { cancelable: true, bubbles: true })
      );
    }
  } catch (_e) {
    // Best-effort — never throw from a post-success side effect.
  }

  setTimeout(() => {
    if (form.parentNode === document.body) {
      document.body.removeChild(form);
    }
  }, 2000);
}
